import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import { api } from "../api";
import AgendaCoverage from "./AgendaCoverage";
import ScheduleStrategy from "./ScheduleStrategy";
import GrowthActions from "./GrowthActions";
import {
  Loader2, Target, Users, Activity, TrendingUp, Stethoscope,
  Building2, Calendar, CalendarDays, Filter, Search, Briefcase, BarChart3,
  Clock, AlertTriangle,
} from "lucide-react";

const MONTHS_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const SPECIALTY_COLORS: Record<string, string> = {
  Cardiologia: "#005F71",
  "Cardiologia Pediátrica": "#3A9A9F",
  Pneumologia: "#217D84",
  "Cirurgia Plástica": "#52B7BB",
  Imunoalergologia: "#6BD4D7",
  "Cirurgia Vascular": "#F59E0B",
};

interface StrategySummary {
  upload_id: number;
  specialties: {
    specialty: string;
    medicos: number | null;
    consultas_ytd: number | null;
    consultas_est_2026: number | null;
    meta_consultas_2027: number | null;
    cirurgias_ytd: number | null;
    meta_cirurgias_2027: number | null;
    crescimento_alvo: string | null;
    ocupacao_atual: number | null;
    vagas_livres: number | null;
    gap_consultas: number | null;
    gap_cirurgias: number | null;
  }[];
}

interface StrategyRow {
  id: number;
  sheet_name: string;
  specialty: string;
  unit?: string | null;
  doctor?: string | null;
  vagas?: number | null;
  consultas?: number | null;
  ocupacao?: number | null;
  vagas_livres?: number | null;
  cirurgias?: number | null;
  consultas_est_2026?: number | null;
  meta_consultas_2027?: number | null;
  gap_consultas_2027?: number | null;
  meta_cirurgias_2027?: number | null;
  gap_cirurgias_2027?: number | null;
  ocupacao_necessaria_2027?: number | null;
  priority_category?: string | null;
  diagnosis?: string | null;
  action_agreed?: string | null;
  responsible?: string | null;
  status?: string | null;
  hours_per_week?: number | null;
  medicos?: number | null;
  crescimento_alvo?: string | null;
  n_unidades?: number | null;
  unidades_list?: string | null;
}

interface AgendaCoverageItem {
  specialty: string;
  unit: string;
  rank: number;
  metrics: Record<string, { value: number | null; raw: string }>;
  periodo_critico: string | null;
  prioridade: string | null;
}

export default function DashboardExecutivo2027() {
  const [summary, setSummary] = useState<StrategySummary | null>(null);
  const [medicoAMedico, setMedicoAMedico] = useState<StrategyRow[]>([]);
  const [porUnit, setPorUnit] = useState<StrategyRow[]>([]);
  const [resumo2027, setResumo2027] = useState<StrategyRow[]>([]);
  const [calculos2027, setCalculos2027] = useState<StrategyRow[]>([]);
  const [medicosMultiUnit, setMedicosMultiUnit] = useState<StrategyRow[]>([]);
  const [dashboardExec, setDashboardExec] = useState<StrategyRow[]>([]);
  const [filters, setFilters] = useState<{ specialties: string[]; units: string[]; doctors: string[] }>({
    specialties: [],
    units: [],
    doctors: [],
  });
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>("");
  const [selectedUnit, setSelectedUnit] = useState<string>("");
  const [searchDoctor, setSearchDoctor] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [agendaCoverage, setAgendaCoverage] = useState<{ items: AgendaCoverageItem[] } | null>(null);
  const [currentTab, setCurrentTab] = useState<"overview" | "medicos" | "unidades" | "metas" | "calculos" | "multiunidade" | "acoes" | "agenda" | "schedules" | "growth">("overview");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const tabsRef = useRef<HTMLDivElement>(null);

  const agendaCriticalUnits = useMemo(() => {
    if (!agendaCoverage) return [];
    return agendaCoverage.items
      .filter((i) => i.prioridade === "Crítica")
      .sort((a, b) => (b.metrics["Horas em falta Total"]?.value || 0) - (a.metrics["Horas em falta Total"]?.value || 0))
      .slice(0, 5);
  }, [agendaCoverage]);

  const agendaTotalHoursMissing = useMemo(() => {
    if (!agendaCoverage) return 0;
    return agendaCoverage.items.reduce((sum, i) => sum + (i.metrics["Horas em falta Total"]?.value || 0), 0);
  }, [agendaCoverage]);

  const agendaCriticalCount = useMemo(() => {
    if (!agendaCoverage) return 0;
    return agendaCoverage.items.filter((i) => i.prioridade === "Crítica").length;
  }, [agendaCoverage]);

  // Sync active tab with URL ?tab= param
  useEffect(() => {
    const tab = searchParams.get("tab");
    const validTabs = ["overview", "medicos", "unidades", "metas", "calculos", "multiunidade", "acoes", "agenda", "schedules", "growth"];
    if (tab && validTabs.includes(tab)) {
      setCurrentTab(tab as any);
    }
  }, [searchParams]);

  // Update URL when tab changes
  useEffect(() => {
    const current = searchParams.get("tab");
    if (current !== currentTab) {
      setSearchParams({ tab: currentTab }, { replace: true });
    }
  }, [currentTab]);

  // Scroll to tabs when navigating with a hash
  useEffect(() => {
    if (location.hash === "#executive-tabs" && tabsRef.current) {
      tabsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, currentTab]);

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      api.getStrategySummary(),
      api.getStrategyMedicoAMedico(),
      api.getStrategyPorUnidade(),
      api.getStrategyResumo2027(),
      api.getStrategyCalculos2027(),
      api.getStrategyMedicosMultiUnidade().catch((err) => { console.warn("medicos-multi-unidade failed", err); return { rows: [] }; }),
      api.getStrategyDashboardExec(),
      api.getStrategyFilters(),
      api.getAgendaCoveragePriorities().catch(() => null),
    ])
      .then((results) => {
        const [sum, med, uni, res, calc, multi, dash, filt, agendaPri] = results.map((r) =>
          r.status === "fulfilled" ? r.value : null
        );
        if (sum) setSummary(sum);
        if (med) setMedicoAMedico(med.rows || []);
        if (uni) setPorUnit(uni.rows || []);
        if (res) setResumo2027(res.rows || []);
        if (calc) setCalculos2027(calc.rows || []);
        if (multi) setMedicosMultiUnit(multi.rows || []);
        if (dash) setDashboardExec(dash.rows || []);
        if (filt) setFilters(filt);
        if (agendaPri && agendaPri.items) {
          setAgendaCoverage(agendaPri);
        }
      })
      .catch((err) => {
        console.error("Failed to load strategy data", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredMedicoAMedico = useMemo(() => {
    return medicoAMedico.filter((r) => {
      const matchesSpec = !selectedSpecialty || r.specialty === selectedSpecialty;
      const matchesUnit = !selectedUnit || r.unit === selectedUnit;
      const matchesDoctor = !searchDoctor || (r.doctor || "").toLowerCase().includes(searchDoctor.toLowerCase());
      return matchesSpec && matchesUnit && matchesDoctor;
    });
  }, [medicoAMedico, selectedSpecialty, selectedUnit, searchDoctor]);

  const filteredPorUnit = useMemo(() => {
    return porUnit.filter((r) => (!selectedSpecialty || r.specialty === selectedSpecialty));
  }, [porUnit, selectedSpecialty]);

  const filteredResumo = useMemo(() => {
    return resumo2027.filter((r) => (!selectedSpecialty || r.specialty === selectedSpecialty));
  }, [resumo2027, selectedSpecialty]);

  const filteredCalculos = useMemo(() => {
    return calculos2027.filter((r) => {
      const matchesSpec = !selectedSpecialty || r.specialty === selectedSpecialty;
      const matchesUnit = !selectedUnit || r.unit === selectedUnit;
      return matchesSpec && matchesUnit;
    });
  }, [calculos2027, selectedSpecialty, selectedUnit]);

  const filteredMulti = useMemo(() => {
    return medicosMultiUnit.filter((r) => {
      const matchesSpec = !selectedSpecialty || r.specialty === selectedSpecialty;
      const matchesDoctor = !searchDoctor || (r.doctor || "").toLowerCase().includes(searchDoctor.toLowerCase());
      return matchesSpec && matchesDoctor;
    });
  }, [medicosMultiUnit, selectedSpecialty, searchDoctor]);

  const totalMedicos = useMemo(() => {
    return (summary?.specialties || []).reduce((acc, s) => acc + (s.medicos || 0), 0);
  }, [summary]);
  const totalConsultations = useMemo(() => {
    return (summary?.specialties || []).reduce((acc, s) => acc + (s.consultas_ytd || 0), 0);
  }, [summary]);
  const totalMetaConsultations = useMemo(() => {
    return (summary?.specialties || []).reduce((acc, s) => acc + (s.meta_consultas_2027 || 0), 0);
  }, [summary]);
  const totalSlotsLivres = useMemo(() => {
    return (summary?.specialties || []).reduce((acc, s) => acc + (s.vagas_livres || 0), 0);
  }, [summary]);

  const summaryChartData = useMemo(() => {
    return (summary?.specialties || []).map((s) => ({
      name: s.specialty,
      consultas: s.consultas_ytd || 0,
      meta: s.meta_consultas_2027 || 0,
    }));
  }, [summary]);

  const unidadeChartData = useMemo(() => {
    const agg: Record<string, number> = {};
    filteredPorUnit.forEach((r) => {
      const k = r.unit || "Sem unidade";
      agg[k] = (agg[k] || 0) + (r.consultas || 0);
    });
    return Object.entries(agg).map(([name, value]) => ({ name, value }));
  }, [filteredPorUnit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-trofa-700" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Executive Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">
          Doctor-to-Doctor Management · Baseline Jan-Aug 2026
        </p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={Users} label="Total Doctors" value={totalMedicos.toLocaleString()} />
        <KpiCard icon={Activity} label="Consultations YTD" value={totalConsultations.toLocaleString()} />
        <KpiCard icon={Target} label="2027 Consultation Target" value={totalMetaConsultations.toLocaleString()} />
        <KpiCard icon={TrendingUp} label="Open Slots YTD" value={totalSlotsLivres.toLocaleString()} />
      </div>

      {/* Dashboard_exec KPIs */}
      {dashboardExec.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          {dashboardExec.map((r) => (
            <KpiCard
              key={r.id}
              icon={Briefcase}
              label={r.specialty || "KPI"}
              value={String(r.medicos ?? r.vagas ?? r.consultas ?? "—")}
            />
          ))}
        </div>
      )}

      {/* Agenda Coverage summary card */}
      {agendaCoverage && agendaCoverage.items.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-trofa-700" />
              <h2 className="font-semibold text-slate-900">Agenda Coverage — Agosto 2026</h2>
            </div>
            <Link
              to="/dashboard-executivo-2027?tab=agenda#executive-tabs"
              className="text-sm text-trofa-700 hover:underline"
            >
              Open full view →
            </Link>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-slate-500">Total hours missing</p>
              <p className="text-2xl font-bold text-slate-900">{agendaTotalHoursMissing.toLocaleString()}h</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Critical units</p>
              <p className="text-2xl font-bold text-red-600">{agendaCriticalCount}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Units tracked</p>
              <p className="text-2xl font-bold text-slate-900">{agendaCoverage.items.length}</p>
            </div>
          </div>
          <div className="px-6 pb-6">
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              Top 5 critical units by hours missing
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Specialty</th>
                    <th className="text-left px-3 py-2 font-medium">Unit</th>
                    <th className="text-right px-3 py-2 font-medium">Total missing</th>
                    <th className="text-left px-3 py-2 font-medium">Critical period</th>
                    <th className="text-left px-3 py-2 font-medium">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {agendaCriticalUnits.map((item) => (
                    <tr key={`${item.specialty}-${item.unit}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">{item.specialty}</td>
                      <td className="px-3 py-2 font-medium text-slate-800">{item.unit}</td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {item.metrics["Horas em falta Total"]?.value ?? "—"}h
                      </td>
                      <td className="px-3 py-2 text-slate-600">{item.periodo_critico || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          item.prioridade === "Crítica" ? "bg-red-100 text-red-700" :
                          item.prioridade === "Alta" ? "bg-orange-100 text-orange-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {item.prioridade || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div ref={tabsRef} id="executive-tabs" className="flex items-center gap-2 mb-6 border-b border-slate-200">
        <TabButton active={currentTab === "overview"} onClick={() => setCurrentTab("overview")} icon={Stethoscope} label="Overview" />
        <TabButton active={currentTab === "medicos"} onClick={() => setCurrentTab("medicos")} icon={Users} label="Doctor to Doctor" />
        <TabButton active={currentTab === "unidades"} onClick={() => setCurrentTab("unidades")} icon={Building2} label="By Unit" />
        <TabButton active={currentTab === "metas"} onClick={() => setCurrentTab("metas")} icon={Target} label="2027 Targets" />
        <TabButton active={currentTab === "calculos"} onClick={() => setCurrentTab("calculos")} icon={Activity} label="2027 Calculations" />
        <TabButton active={currentTab === "multiunidade"} onClick={() => setCurrentTab("multiunidade")} icon={Building2} label="Multi-Unit Doctors" />
        <TabButton active={currentTab === "acoes"} onClick={() => setCurrentTab("acoes")} icon={Target} label="Actions" />
        <TabButton active={currentTab === "agenda"} onClick={() => setCurrentTab("agenda")} icon={CalendarDays} label="Agenda Coverage" />
        <TabButton active={currentTab === "schedules"} onClick={() => setCurrentTab("schedules")} icon={Activity} label="Schedules vs Strategy" />
        <TabButton active={currentTab === "growth"} onClick={() => setCurrentTab("growth")} icon={TrendingUp} label="Growth Actions 2027" />
      </div>

      {/* Filters — only for native executive tabs */}
      {!['agenda', 'schedules', 'growth'].includes(currentTab) && (
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Filters:</span>
        </div>
        <select
          value={selectedSpecialty}
          onChange={(e) => setSelectedSpecialty(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
        >
          <option value="">All specialties</option>
          {filters.specialties.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {currentTab === "medicos" && (
          <>
            <select
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
            >
              <option value="">All units</option>
              {filters.units.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search doctor..."
                value={searchDoctor}
                onChange={(e) => setSearchDoctor(e.target.value)}
                className="border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
              />
            </div>
          </>
        )}

        <button
          onClick={async () => {
            setReportLoading(true);
            const win = window.open("", "_blank");
            if (!win) {
              alert("Could not open report. Please allow popups for this site.");
              setReportLoading(false);
              return;
            }
            try {
              const spec = selectedSpecialty || "";
              const html = await api.getDirectorBriefStrategyHtml(spec);
              const blob = new Blob([html], { type: "text/html" });
              const url = URL.createObjectURL(blob);
              win.location.href = url;
            } catch (err) {
              console.error(err);
              alert("Failed to generate report. Check console.");
              win.close();
            } finally {
              setReportLoading(false);
            }
          }}
          disabled={reportLoading}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-trofa-700 rounded-lg hover:bg-trofa-800 transition-colors disabled:opacity-70"
        >
          {reportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
          Report
        </button>
      </div>
      )}

      {/* Tab: Overview */}
      {currentTab === "overview" && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Specialty Summary</h2>
              <span className="text-xs text-slate-400">Baseline Jan-Aug 2026</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Specialty</th>
                    <th className="text-right px-4 py-3 font-medium">Doctors</th>
                    <th className="text-right px-4 py-3 font-medium">Consultations YTD</th>
                    <th className="text-right px-4 py-3 font-medium">2027 Target</th>
                    <th className="text-right px-4 py-3 font-medium">Open Slots</th>
                    <th className="text-right px-4 py-3 font-medium">Growth Target</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.specialties || []).map((s) => (
                    <tr key={s.specialty} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{s.specialty}</td>
                      <td className="px-4 py-3 text-right">{s.medicos ?? "—"}</td>
                      <td className="px-4 py-3 text-right">{s.consultas_ytd?.toLocaleString() ?? "—"}</td>
                      <td className="px-4 py-3 text-right">{s.meta_consultas_2027?.toLocaleString() ?? "—"}</td>
                      <td className="px-4 py-3 text-right">{s.vagas_livres?.toLocaleString() ?? "—"}</td>
                      <td className="px-4 py-3 text-right">{s.crescimento_alvo ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Consultations YTD vs 2027 Target</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summaryChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="consultas" name="Consultations YTD" fill="#005F71" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="meta" name="2027 Target" fill="#3A9A9F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Consultations by Unit</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={unidadeChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3A9A9F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Doctor to Doctor */}
      {currentTab === "medicos" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Doctor to Doctor</h2>
            <span className="text-xs text-slate-400">{filteredMedicoAMedico.length} records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Doctor</th>
                  <th className="text-left px-4 py-3 font-medium">Specialty</th>
                  <th className="text-left px-4 py-3 font-medium">Unit</th>
                  <th className="text-right px-4 py-3 font-medium">Slots</th>
                  <th className="text-right px-4 py-3 font-medium">Consultations</th>
                  <th className="text-right px-4 py-3 font-medium">Occupancy</th>
                  <th className="text-right px-4 py-3 font-medium">Open Slots</th>
                  <th className="text-left px-4 py-3 font-medium">Diagnosis</th>
                  <th className="text-left px-4 py-3 font-medium">Agreed Action</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredMedicoAMedico.slice(0, 50).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.doctor || "—"}</td>
                    <td className="px-4 py-3">{r.specialty}</td>
                    <td className="px-4 py-3">{r.unit || "—"}</td>
                    <td className="px-4 py-3 text-right">{r.vagas?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.consultas?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.ocupacao != null ? `${Math.round(r.ocupacao)}%` : "—"}</td>
                    <td className="px-4 py-3 text-right">{r.vagas_livres?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 max-w-xs truncate" title={r.diagnosis || ""}>{r.diagnosis || "—"}</td>
                    <td className="px-4 py-3 max-w-xs truncate" title={r.action_agreed || ""}>{r.action_agreed || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(r.status)}`}>
                        {r.status || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: By Unit */}
      {currentTab === "unidades" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">By Unit</h2>
            <span className="text-xs text-slate-400">{filteredPorUnit.length} records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Unit</th>
                  <th className="text-left px-4 py-3 font-medium">Specialty</th>
                  <th className="text-right px-4 py-3 font-medium">Consultations</th>
                  <th className="text-right px-4 py-3 font-medium">Slots</th>
                  <th className="text-right px-4 py-3 font-medium">Occupancy</th>
                </tr>
              </thead>
              <tbody>
                {filteredPorUnit.slice(0, 50).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.unit || "—"}</td>
                    <td className="px-4 py-3">{r.specialty}</td>
                    <td className="px-4 py-3 text-right">{r.consultas?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.vagas?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.ocupacao != null ? `${Math.round(r.ocupacao)}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: 2027 Targets */}
      {currentTab === "metas" && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-900 mb-4">2027 Summary — Targets and Gaps</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Specialty</th>
                  <th className="text-right px-4 py-3 font-medium">Est. Cons 2026</th>
                  <th className="text-right px-4 py-3 font-medium">2027 Cons Target</th>
                  <th className="text-right px-4 py-3 font-medium">Cons Gap</th>
                  <th className="text-right px-4 py-3 font-medium">2027 Surgery Target</th>
                  <th className="text-right px-4 py-3 font-medium">Surgery Gap</th>
                  <th className="text-left px-4 py-3 font-medium">Category</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredResumo.slice(0, 50).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.specialty}</td>
                    <td className="px-4 py-3 text-right">{r.consultas_est_2026?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.meta_consultas_2027?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.gap_consultas_2027?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.meta_cirurgias_2027?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.gap_cirurgias_2027?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3">{r.priority_category || "—"}</td>
                    <td className="px-4 py-3">{r.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: 2027 Calculations */}
      {currentTab === "calculos" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">2027 Calculations — Doctor × Unit</h2>
            <span className="text-xs text-slate-400">{filteredCalculos.length} records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Doctor</th>
                  <th className="text-left px-4 py-3 font-medium">Specialty</th>
                  <th className="text-left px-4 py-3 font-medium">Unit</th>
                  <th className="text-right px-4 py-3 font-medium">2027 Cons Target</th>
                  <th className="text-right px-4 py-3 font-medium">2027 Surgery Target</th>
                  <th className="text-right px-4 py-3 font-medium">Required Occupancy</th>
                  <th className="text-right px-4 py-3 font-medium">Hours/Week</th>
                </tr>
              </thead>
              <tbody>
                {filteredCalculos.slice(0, 50).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.doctor || "—"}</td>
                    <td className="px-4 py-3">{r.specialty}</td>
                    <td className="px-4 py-3">{r.unit || "—"}</td>
                    <td className="px-4 py-3 text-right">{r.meta_consultas_2027?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.meta_cirurgias_2027?.toLocaleString() ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.ocupacao_necessaria_2027 != null ? `${Math.round(r.ocupacao_necessaria_2027)}%` : "—"}</td>
                    <td className="px-4 py-3 text-right">{r.hours_per_week ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Multi-Unit Doctors */}
      {currentTab === "multiunidade" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Multi-Unit Doctors</h2>
            <span className="text-xs text-slate-400">{filteredMulti.length} records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Doctor</th>
                  <th className="text-left px-4 py-3 font-medium">Specialty</th>
                  <th className="text-right px-4 py-3 font-medium">Nº Units</th>
                  <th className="text-left px-4 py-3 font-medium">Units</th>
                </tr>
              </thead>
              <tbody>
                {filteredMulti.slice(0, 50).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.doctor || "—"}</td>
                    <td className="px-4 py-3">{r.specialty}</td>
                    <td className="px-4 py-3 text-right">{r.n_unidades ?? "—"}</td>
                    <td className="px-4 py-3">{r.unidades_list || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Actions */}
      {currentTab === "acoes" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Action Plan — Review</h2>
            <span className="text-xs text-slate-400">{medicoAMedico.filter((r) => r.action_agreed || r.diagnosis || r.status).length} records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Doctor</th>
                  <th className="text-left px-4 py-3 font-medium">Specialty</th>
                  <th className="text-left px-4 py-3 font-medium">Unit</th>
                  <th className="text-left px-4 py-3 font-medium">Diagnosis</th>
                  <th className="text-left px-4 py-3 font-medium">Agreed Action / Revert</th>
                  <th className="text-left px-4 py-3 font-medium">Owner</th>
                  <th className="text-left px-4 py-3 font-medium">Next Meeting</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {medicoAMedico
                  .filter((r) => (!selectedSpecialty || r.specialty === selectedSpecialty) && (r.action_agreed || r.diagnosis || r.status))
                  .slice(0, 100)
                  .map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.doctor || "—"}</td>
                      <td className="px-4 py-3">{r.specialty}</td>
                      <td className="px-4 py-3">{r.unit || "—"}</td>
                      <td className="px-4 py-3 max-w-xs">{r.diagnosis || "—"}</td>
                      <td className="px-4 py-3 max-w-md">{r.action_agreed || "—"}</td>
                      <td className="px-4 py-3">{r.responsible || "—"}</td>
                      <td className="px-4 py-3">{r.next_meeting || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(r.status)}`}>
                          {r.status || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {currentTab === "agenda" && <AgendaCoverage embedded />}
      {currentTab === "schedules" && <ScheduleStrategy embedded />}
      {currentTab === "growth" && <GrowthActions embedded />}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4">
      <div className="p-2.5 bg-trofa-50 rounded-lg">
        <Icon className="w-5 h-5 text-trofa-700" />
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: any; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-trofa-700 text-trofa-700"
          : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function statusBadgeClass(status: string | null | undefined): string {
  if (!status) return "bg-slate-100 text-slate-600";
  const s = status.toLowerCase();
  if (s.includes("concluído") || s.includes("concluido") || s.includes("done")) return "bg-green-100 text-green-700";
  if (s.includes("iniciar") || s.includes("por iniciar")) return "bg-amber-100 text-amber-700";
  if (s.includes("atraso") || s.includes("delay")) return "bg-red-100 text-red-700";
  return "bg-blue-100 text-blue-700";
}
